import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { Campaign, SearchTargetable } from '@feathr/blackbox';
import { TargetableClass } from '@feathr/blackbox';
import { ContextMenu } from '@feathr/components';
import { useStore } from '@feathr/extender/state';
import { getIconForAction } from '@feathr/hooks';

interface IProps {
  campaign: Campaign;
}

/**
 * Offers the keyword list a Search Keyword campaign's audience was generated from. The terms
 * themselves are never persisted, so we hand back the CSV the user originally uploaded.
 */
function DownloadKeywordListMenuItem({ campaign }: Readonly<IProps>): JSX.Element | null {
  const { t } = useTranslation();
  const { Targetables, Targetings } = useStore();

  /*
   * Returning before the list call keeps every other campaign type request-free. Menu items
   * only mount once the dropdown opens, so this runs on menu open rather than page load.
   */
  if (!campaign.isSearchKeywordCampaign) {
    return null;
  }

  const targetings = Targetings.list({
    filters: { _parent: campaign.id, is_archived__ne: true, kind: 'search' },
  });
  // A Search Keyword campaign is limited to exactly one search targeting.
  const [searchTargeting] = targetings.models;
  const targetableId = searchTargeting?.get('target_data');

  /*
   * The _cls override guarantees disambiguateTargetable returns a SearchTargetable, the same
   * narrowing AdWizardAudienceStep.useTargetable relies on.
   */
  const targetable = targetableId
    ? (Targetables.get(targetableId, { _cls: TargetableClass.search }) as SearchTargetable)
    : undefined;

  const url = targetable?.get('src_search_keywords');
  const isPending = targetings.isPending || !!targetable?.isPending;
  const isErrored = targetings.isErrored || !!targetable?.isErrored;

  /*
   * A failed lookup must not read as "this campaign never had a keyword list", since the two
   * call for completely different things from the user.
   */
  let tooltip: string | undefined;
  if (!url && !isPending) {
    tooltip = isErrored
      ? t("We couldn't load this keyword list. Refresh the page to try again.")
      : t('This campaign has no keyword list to download.');
  }

  /*
   * Button renders an anchor whenever href is set, and an anchor ignores disabled, so href
   * must be left undefined while there is nothing to download.
   *
   * The file lives on S3, so `download` is cross-origin and the browser ignores it: the saved
   * name is the storage key, not src_filename. Kept because it is the correct intent and does
   * take effect if the file is ever served same-origin.
   */
  return (
    <ContextMenu.Item
      disabled={!url}
      download={targetable?.get('src_filename')}
      href={url}
      name={'download-keyword-list'}
      prefix={getIconForAction('download')}
      target={'_blank'}
      tooltip={tooltip}
    >
      {t('Download keyword list')}
    </ContextMenu.Item>
  );
}

export default observer(DownloadKeywordListMenuItem);
